import type { ReactNode } from "react";
import { cx } from "./utils";

/** One column definition. `render` receives the row and its index. */
export interface Column<R> {
  /** Stable key, also used as the stacked-card label. */
  key: string;
  /** Header cell content. */
  header: ReactNode;
  /** Cell renderer. */
  render: (row: R, index: number) => ReactNode;
  /** Text alignment. @default "start" */
  align?: "start" | "center" | "end";
  /** Fixed column width (desktop only). */
  width?: string;
  /** Hide this column in the <768px stacked-card layout. */
  hideOnMobile?: boolean;
}

export interface TableProps<R> {
  columns: ReadonlyArray<Column<R>>;
  rows: readonly R[];
  /** Row identity. */
  rowKey: (row: R, index: number) => string;
  /** Accessible name for the table. */
  caption?: string;
  /** Row click handler; makes rows focusable and Enter-activatable. */
  onRowClick?: (row: R, index: number) => void;
  /** Rendered instead of the body when `rows` is empty. */
  empty?: ReactNode;
  /** Keep the real table layout below 768px instead of stacking into cards. */
  noStack?: boolean;
  className?: string;
}

/**
 * Data table that collapses to stacked cards below 768px (each cell gets a
 * `data-label` used as the CSS-generated row label).
 */
export function Table<R>({
  columns,
  rows,
  rowKey,
  caption,
  onRowClick,
  empty,
  noStack = false,
  className,
}: TableProps<R>) {
  if (rows.length === 0 && empty != null) {
    return <div className="xz-table__empty">{empty}</div>;
  }
  return (
    <div className={cx("xz-table-wrap", !noStack && "xz-table-wrap--stack", className)}>
      <table className="xz-table">
        {caption && <caption className="xz-sr-only">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                style={c.width ? { width: c.width } : undefined}
                data-align={c.align ?? "start"}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={rowKey(row, i)}
              className={cx(onRowClick && "is-clickable")}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row, i) : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRowClick(row, i);
                      }
                    }
                  : undefined
              }
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  data-label={typeof c.header === "string" ? c.header : c.key}
                  data-align={c.align ?? "start"}
                  className={cx(c.hideOnMobile && "xz-hide-mobile")}
                >
                  {c.render(row, i)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
